import { sendAmplitudeEvent } from './utils'

/**
 * Common shape for amplitude event property bags. Studio apps and shared
 * route packages extend this with their own per-event fields.
 */
export interface BaseEventProps {
  userId?: string
  [key: string]: unknown
}

const debug = false

/**
 * Build a typed event tracker bound to a single event name. Used by Studio
 * apps and shared `@repo/studio-routes-*` packages to centralise the
 * `sendAmplitudeEvent` plumbing while keeping each call site readable.
 *
 * The generic `TEvent` constraint stays as `string` so this helper works
 * for any app's event vocabulary; apps that want to enforce a closed
 * union of event names should wrap this with their own narrower helper.
 */
export function createEventTracker<
  TEvent extends string,
  TProps extends Record<string, unknown> | undefined = BaseEventProps,
>(eventName: TEvent) {
  return (props?: TProps) => {
    if (debug) {
      // oxlint-disable-next-line no-console
      console.log('🎃 Event:', eventName, props)
    }
    // Preserve the old 3-arg shape (eventName, properties, callback) of the
    // legacy apps/studio createEventTracker so existing call assertions
    // (e.g. social-invite.test.tsx) keep matching.
    sendAmplitudeEvent(eventName as never, props as Record<string, unknown> | undefined, undefined)
  }
}

/**
 * Sugar over `createEventTracker` for the common `'<Feature> - <Event>'`
 * naming convention used across Studio amplitude events.
 */
export function createToolEvent<
  TFeature extends string,
  TEvent extends string,
  TProps extends BaseEventProps = BaseEventProps,
>(feature: TFeature, event: TEvent) {
  return createEventTracker<`${TFeature} - ${TEvent}`, TProps & BaseEventProps>(
    `${feature} - ${event}` as const,
  )
}
